import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, resolveImg, isNew } from "@/lib/adminApi";

export default function Articles() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get(`/articles`).then((r) => setArticles(r.data || [])).catch(() => setArticles([]));
  }, []);

  return (
    <section
      id="journal"
      data-testid="articles-section"
      className="py-28 md:py-40"
      style={{ background: "var(--surface)" }}
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <div className="eyebrow mb-4">The Journal</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-bone">
              Articles &amp; <em className="italic" style={{ color: "var(--amber)" }}>essays</em>.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="hairline w-16" />
            <span className="eyebrow">{articles.length} Entries</span>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {articles.map((a, i) => (
            <motion.article
              key={a.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`article-${a.id}`}
              className={`${i === 0 ? "col-span-12 lg:col-span-8 lg:row-span-2" : "col-span-12 md:col-span-6 lg:col-span-4"}`}
            >
              <Link to={`/journal/${a.slug || a.id}`} className="tile-frame flex flex-col h-full" data-testid={`article-link-${a.id}`}>
              <div className={`relative overflow-hidden ${i === 0 ? "aspect-[16/12]" : "aspect-[4/5]"}`}>
                <img src={resolveImg(a.image_url)} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)" }} />
                <div className="absolute top-5 left-5 flex items-center gap-3">
                  <span className="chapter-numeral text-3xl">{a.chapter_no}</span>
                  <span className="eyebrow" style={{ color: "var(--bone)" }}>{a.category}</span>
                </div>
                {isNew(a.created_at) && (
                  <span className="new-badge absolute bottom-5 left-5" data-testid={`article-new-${a.id}`}>
                    <span className="dot" /> New
                  </span>
                )}
              </div>
              <div className="p-6 md:p-8 flex-1 flex flex-col justify-between gap-6">
                <div>
                  <h3 className={`font-display leading-[1.05] tracking-tight text-bone ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"} mb-3`}>
                    {a.title}
                  </h3>
                  <p className="font-body text-base md:text-lg text-bone-dim">{a.dek}</p>
                </div>
                <div className="flex items-center justify-between border-t pt-4" style={{ borderColor: "var(--hairline)" }}>
                  <div>
                    <div className="eyebrow" style={{ color: "var(--bone-dim)" }}>By {a.author}</div>
                    <div className="font-accent text-sm text-brass mt-1" style={{ letterSpacing: "0.14em" }}>
                      {a.reading_time}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 font-accent text-xs uppercase tracking-[0.22em]">
                    Read <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
